import React, { useCallback, useMemo, useState } from 'react';
import { SidePanel } from '@securspace/securspace-ui-kit';
import PaymentModalBody from "./payment/PaymentModalBody";
import RedesignMicroDepositForm from "./payment/RedesignMicroDepositForm";
import StripeCardForm from "./payment/StripeCardForm";
import PlaidService from "../components/paymentMethods/processors/PlaidService";
import {useSnackbar} from "../context/snackbar-context";
import {Account} from "../types/Account";
import {getErrorMessage} from "../util/NetworkErrorUtil";

interface IRedesignAddPaymentMethodSetupProps {
  account: Account,
  collectMicroDepositVerifyPayment: boolean,
  doneWithAddPaymentMethod: () => void,
  loadPaymentMethods: () => void,
  microDepositVerify: boolean,
  payWithAch: boolean,
  paymentModalOpen: boolean,
  setACH: (value: boolean) => void,
  setMicroDeposit: (value: boolean) => void,
  setPaymentModalOpen: (value: boolean) => void,
}

const RedesignAddPaymentMethodSetup = (props: IRedesignAddPaymentMethodSetupProps) => {
  const {
    account,
    collectMicroDepositVerifyPayment,
    doneWithAddPaymentMethod,
    loadPaymentMethods,
    microDepositVerify,
    payWithAch,
    paymentModalOpen,
    setACH,
    setMicroDeposit,
    setPaymentModalOpen,
  } = props;

  const {snackbarShowMessage} = useSnackbar();

  const [showMicroDepositVerify, setShowMicroDepositVerify] = useState(false);
  const [showCardForm, setShowCardForm] = useState(false);

  const handleModalClose = useCallback(() => {
    setPaymentModalOpen(!paymentModalOpen);
    setShowMicroDepositVerify(false);
    setShowCardForm(false);
    loadPaymentMethods();
  }, [setPaymentModalOpen, paymentModalOpen, setShowMicroDepositVerify, loadPaymentMethods]);

  const handlePlaidFailure = useCallback((error: any) => {
    if (error?.requiresAction) {
      snackbarShowMessage(
        error.message || 'Your bank requires additional verification. Please check your email to complete the process.',
        'warning',
        15000
      );
      loadPaymentMethods();
    } else {
      snackbarShowMessage(
        getErrorMessage(error, error?.message || 'Could not start bank account setup. Please try again later.'),
        'error',
        15000
      );
      handleModalClose();
    }
  }, [handleModalClose, snackbarShowMessage, loadPaymentMethods]);

  const plaid = useMemo(() => {
    const authority = {
      id: account.id,
      email: account.email,
      userType: account.userType
    };
    const plaidEnv = {
      plaidEnvironment: account.plaidEnvironment,
      plaidClientName: account.plaidClientName,
      plaidPublicKey: account.plaidPublicKey,
    };
    return new PlaidService(plaidEnv, authority, handleModalClose, handlePlaidFailure);
  }, [account, handleModalClose, handlePlaidFailure]);

  const launchAddPaymentMethod = () => {
    if (collectMicroDepositVerifyPayment) {
      setShowMicroDepositVerify(true);
    } else if (!payWithAch && !collectMicroDepositVerifyPayment) {
      handleStripePayment();
    } else {
      handlePlaidPayment();
    }
  };

  const handlePlaidPayment = () => {
    if (account.id) {
      plaid.openPlaidHandler();
    } else {
      snackbarShowMessage("There was an error launching Payment Service. Please try again later.", 'error', 15000);
    }
  }

  const handleStripePayment = () => {
    if (account.id) {
      setShowCardForm(true);
    } else {
      snackbarShowMessage("There was an error launching Payment Service. Please try again later.", 'error', 15000);
    }
  }

  const panelTitle = showCardForm
    ? 'Add Card Payment Method'
    : showMicroDepositVerify
      ? 'Add Payment Method'
      : 'Add Payment Method';

  return <SidePanel
    open={paymentModalOpen}
    onClose={handleModalClose}
    title={panelTitle}
  >
      {showCardForm ?
        <StripeCardForm
          platformPublishedKey={account.platformPublishedKey}
          email={account.email}
          onSuccess={handleModalClose}
          onCancel={() => setShowCardForm(false)}
        />
      : !showMicroDepositVerify ?
        <PaymentModalBody
          setACH={setACH}
          payWithAch={payWithAch}
          collectMicroDepositVerifyPayment={collectMicroDepositVerifyPayment}
          setMicroDeposit={setMicroDeposit}
          microDepositVerify={microDepositVerify}
          doneWithAddPaymentMethod={doneWithAddPaymentMethod}
          launchAddPaymentMethod={launchAddPaymentMethod}
        />
        :
        <RedesignMicroDepositForm
          account={account}
          setShowMicroDepositVerify={setShowMicroDepositVerify}
          handleModalClose={handleModalClose}
        />
      }
  </SidePanel>
};

export default RedesignAddPaymentMethodSetup;
